import React, { useCallback, useEffect, useState } from "react"
import { Outlet, useNavigate } from "react-router-dom"
import { Layout, Button, Flex, Spin } from "antd"
import DefaultErrorBoundary from "modules/errors/DefaultErrorBoundary"
import { isEqual, omit } from "lodash"
import useSearchFilter from "hooks/useSearchFilter"
import moment from "moment"
import { useDispatch, useSelector } from "react-redux"
import { DATE_FORMAT } from "modules/datepicker/constants"
import styles from "./Calendar.module.css"
import Header from "./Header"
import CalendarViewSwitcher from "./CalendarViewSwitcher"
import { getCalendarBookings, getCalendarFilters, normalizeCalendarFilters, setSavedFilters, updateCalendarFilters } from "store/calendar"
import { DEFAULT_START_HOUR, DEFAULT_END_HOUR } from "./constants"
import { fetchExperiences } from "store/experiences"
import { getCalendarCrew } from "store/staff"
import { ExitScheduleTimeOff, Journal, ScheduleTimeOff } from "./Modals"
import { useCalendarDateRange } from "./useCalendarDateRange"
import { useDevice } from "hooks/useDevice"
import { useMediaQuery } from "react-responsive"
import { useTranslation } from "react-i18next"
import { routes } from "router"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import CustomDrawer from "ui/Drawer"
function Calendar() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isMobile, isTablet, isDesktop } = useDevice()
  const isWideDesktop = useMediaQuery({ query: "(min-width: 1280px)" })
  const { t } = useTranslation()
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()
  const canCreateBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.CREATE)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const [actionsDrawerOpen, setActionsDrawerOpen] = useState(false)
  const savedFilters = useSelector((state) => state.calendar?.savedFilters)
  const filtersReady = useSelector((state) => state.calendar?.filtersReady || false)
  const isLoading = useSelector((state) => state.calendar?.loading || false)
  const hasMyBookings = useSelector((state) => state.calendar?.hasMyBookings)
  const [startHour, setStartHour] = useState(DEFAULT_START_HOUR)
  const [endHour, setEndHour] = useState(DEFAULT_END_HOUR)
  const [params, updateSearchFilters] = useSearchFilter({
    accessAttributes: ["range", "date", "agenda", "weather", "trip_id", "journal_date", "unavailable_id", "tab", "listview"]
  })
  const [paramsState, setParamsState] = useState(params)
  // Fetch saved filters once on mount — on remount after nav, filtersReady is already true
  // so this is a no-op and savedFilters already has the correct values in Redux
  useEffect(() => {
    if (filtersReady) return
    dispatch(getCalendarFilters()).catch(() => {})
  }, [dispatch, filtersReady])

  // Keep paramsState in sync with params only when they actually differ
  useEffect(() => {
    if (!isEqual(paramsState, params)) {
      setParamsState(params)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)])

  // Ensure default to month view, correct date, and tab in URL
  useEffect(() => {
    const today = moment().format(DATE_FORMAT);
    let changed = false;
    const newParams = { ...params };
    if (!params.range) {
      newParams.range = "month";
      changed = true;
    }
    if (!params.date) {
      newParams.date = today;
      changed = true;
    }
    if (!params.tab) {
      newParams.tab = "all-bookings";
      changed = true;
    }
    if (changed) {
      updateSearchFilters(newParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // (Removed unused calendarDate variable)
  // Consolidated modal state for maintainability
  const [modals, setModals] = useState({ scheduleTimeOff: false, exitScheduleTimeOff: false, journal: false })
  // Preserve existing setter APIs via thin wrappers
  const setIsScheduleTimeOffModalOpen = (open) => setModals((m) => ({ ...m, scheduleTimeOff: open }))
  const setIsExitScheduleTimeOffModalOpen = (open) => setModals((m) => ({ ...m, exitScheduleTimeOff: open }))
  const setIsJournalModalOpen = (open) => setModals((m) => ({ ...m, journal: open }))

  // Handle filter changes from Header: update Redux immediately + persist to API
  const handleFiltersChange = useCallback((newFilters) => {
    const normalized = normalizeCalendarFilters(newFilters)
    dispatch(setSavedFilters(normalized))
    dispatch(updateCalendarFilters(normalized)).catch(() => {})
  }, [dispatch])

  const dates = useCalendarDateRange(params)

  useEffect(() => {
    dispatch(getCalendarCrew())
    dispatch(fetchExperiences("calendar"))
  }, [dispatch])

  const changeHandler = useCallback(
    ({ target, extra }) =>
      setParamsState((prevParamsState) => ({
        ...prevParamsState,
        [target.name]: target.value,
        ...(extra && { [extra.name]: extra.value })
      })),
    []
  )

  useEffect(() => {
    // Only update search filters if paramsState differs from params (ignoring journal_date, unavailable_id)
    if (!isEqual(paramsState, omit(params, ["journal_date", "unavailable_id"]))) {
      updateSearchFilters(paramsState)
    }
  }, [paramsState, updateSearchFilters, params])

  useEffect(() => {
    if (!filtersReady) return
    if (!dates?.start || !dates?.end) return

    const apiParams = new URLSearchParams()

    if (params.range === "date") {
      apiParams.set("date", dates.start)
    } else {
      apiParams.set("start_date", dates.start)
      apiParams.set("end_date", dates.end)
    }

    if (params.tab === "my-bookings") {
      apiParams.set("view_type", "my_bookings")
    }

    if (savedFilters.search) apiParams.set("search", savedFilters.search)
    savedFilters.profile_ids?.forEach((id) => apiParams.append("profile_ids[]", id))
    savedFilters.equipment_ids?.forEach((id) => apiParams.append("boat_ids[]", id))
    savedFilters.category_ids?.forEach((id) => apiParams.append("category_ids[]", id))
    savedFilters.experience_ids?.forEach((id) => apiParams.append("experience_ids[]", id))
    savedFilters.role_ids?.forEach((id) => apiParams.append("role_ids[]", id))
    if (savedFilters.unassigned) apiParams.set("unassigned", "true")
    if (savedFilters.unassigned_equipment) apiParams.set("unassigned_equipment", "true")

    dispatch(getCalendarBookings(apiParams))
  }, [filtersReady, dates?.start, dates?.end, params.range, params.tab, savedFilters, dispatch])


  useEffect(() => {
    if (!dates?.start || !dates?.end) return
    const data = {}
    const startMoment = moment(dates.start)
    const endMoment = moment(dates.end)

    if (params.trip_id) data.trip_id = params.trip_id

    data.forecast_date = []
    while (startMoment.isSameOrBefore(endMoment)) {
      data.forecast_date.push(startMoment.format(DATE_FORMAT))
      startMoment.add(1, "day")
    }
  }, [dates?.start, dates?.end, params?.trip_id, params?.weather, dispatch])

  const handleCancelScheduleTimeOffModal = () => {
    setIsExitScheduleTimeOffModalOpen(true)
  }
  const handleJournalCancel = () => {
    setIsJournalModalOpen(false)
  }

  const handleCancelExitScheduleTimeOffModal = () => {
    setIsExitScheduleTimeOffModalOpen(false)
  }

  const handleYesExitScheduleTimeOffModal = () => {
    setIsExitScheduleTimeOffModalOpen(false)
    setIsScheduleTimeOffModalOpen(false)
  }

  const handleJournalClick = () => {
    setIsJournalModalOpen(true)
  }

  return (
    <>
      {isLoading && (
        <div className={styles.calendarOverlay}>
          <Spin size="large" />
        </div>
      )}
      <Layout className={styles.container} id="dashboard">
        <div className={styles.header}>
          <Header
            params={params}
            onChangeFilters={changeHandler}
            handleJournalClick={handleJournalClick}
            setIsScheduleTimeOffModalOpen={setIsScheduleTimeOffModalOpen}
            onFiltersChange={handleFiltersChange}
            startHour={startHour}
            endHour={endHour}
            onStartHourChange={setStartHour}
            onEndHourChange={setEndHour}
            onResetHours={() => { setStartHour(DEFAULT_START_HOUR); setEndHour(DEFAULT_END_HOUR) }}
            hasMyBookings={hasMyBookings}
            savedFilters={savedFilters}
          />
        </div>
        <div className={styles.calendarWrapper}>
          <div className={styles.calendar}>
            {(isMobile || isTablet) && (
              <div className={styles.mobileGoToToday}>
                <Button
                  color="primary"
                  type="default"
                  variant="outlined"
                  className={styles.goToToday}
                  disabled={moment(params.date).isSame(moment(), "day")}

                  onClick={() => {
                    const today = moment().format(DATE_FORMAT)
                    changeHandler({ target: { name: "date", value: today } })
                  }}
                >
                  Go to Today
                </Button>
              </div>
            )}
            <CalendarViewSwitcher
              onChangeFilters={changeHandler}
              experienceIds={savedFilters.experience_ids || []}
              crewIds={[
                ...(savedFilters.unassigned ? ["unassigned"] : []),
                ...(savedFilters.profile_ids || [])
              ]}
              equipmentIds={[
                ...(savedFilters.unassigned_equipment ? ["unassigned"] : []),
                ...(savedFilters.equipment_ids || [])
              ]}
              categoryIds={savedFilters.category_ids || []}
              roleIds={savedFilters.role_ids || []}
              startHour={startHour}
              endHour={endHour}
            />
          </div>
        </div>
        <DefaultErrorBoundary>
          <Outlet />
        </DefaultErrorBoundary>
      </Layout>
      {modals.journal && (
        <Journal
          open={modals.journal}
          setOpen={setIsJournalModalOpen}
          onCancel={handleJournalCancel}
          selectedDate={params.date || moment().format(DATE_FORMAT)}
          startDate={dates?.start || params.date || moment().format(DATE_FORMAT)}
          endDate={dates?.end || params.date || moment().format(DATE_FORMAT)}
          range={params.range}
        />
      )}
      {modals.scheduleTimeOff && (
        <ScheduleTimeOff
          dates={dates}
          open={modals.scheduleTimeOff}
          onCancel={handleCancelScheduleTimeOffModal}
          setIsScheduleTimeOffModalOpen={setIsScheduleTimeOffModalOpen}
        />
      )}
      {modals.exitScheduleTimeOff && (
        <ExitScheduleTimeOff
          open={modals.exitScheduleTimeOff}
          onCancel={handleCancelExitScheduleTimeOffModal}
          onYes={handleYesExitScheduleTimeOffModal}
        />
      )}
      {(isMobile || isTablet || (isDesktop && !isWideDesktop)) && (
        <>
          <div className={styles.fab}>
            <button
              type="button"
              className={styles.fabButton}
              onClick={() => setActionsDrawerOpen(true)}
              aria-label="Actions"
            >
              +
            </button>
          </div>
          <CustomDrawer open={actionsDrawerOpen} setOpen={setActionsDrawerOpen} title="Actions">
            <Flex vertical gap={12}>
              {canCreateBooking && (
                <Button
                  block
                  size="large"
                  className={styles.actionSheetButton}
                  onClick={() => { setActionsDrawerOpen(false); if (active) navigate(routes.guideBookingNewPath()); }}
                >
                  {t("bookings.add")}
                </Button>
              )}
              {(params.tab === 'my-bookings' || params.tab === 'crew') && (
                <Button
                  block
                  size="large"
                  className={styles.actionSheetButton}
                  onClick={() => { setActionsDrawerOpen(false); if (active) setIsScheduleTimeOffModalOpen(true); }}
                >
                  {t("unavailable_days.schedule_time_off")}
                </Button>
              )}
              {canCreateJournal && (
                <Button
                  block
                  size="large"
                  className={styles.actionSheetButton}
                  onClick={() => { setActionsDrawerOpen(false); if (active) handleJournalClick(); }}
                >
                  {t("calendar.journal.add_journal")}
                </Button>
              )}
            </Flex>
          </CustomDrawer>
        </>
      )}
    </>
  )
}

export default Calendar
